import type { components } from "../api/schema";
import { Badge } from "../ui";

export type CapacityWaitCause = components["schemas"]["CapacityWaitCause"];

export interface CapacityWaitStatusProps {
  isWaitingForCapacity: boolean;
  // Which admission ceiling holds the work (generated `capacity_wait_cause`).
  // Null/undefined keeps the generic label: the wait has no recorded cause.
  capacityWaitCause?: CapacityWaitCause | null;
}

const CAUSE_LABELS: Record<CapacityWaitCause, string> = {
  platform: "queued: waiting for platform capacity",
  organization: "queued: waiting for organization capacity",
  batch: "queued: waiting for batch capacity",
  provider: "queued: waiting for provider capacity",
  // A configuration gap, not saturation: the organization has no positive
  // org_concurrency_max row, so nothing of its can be admitted.
  organization_limit_missing:
    "queued: organization concurrency limit is not configured",
  // Execution-time deferral: every counted slot was held, but no pooled
  // telephony number was free for this attempt (docs/03 §9.1a).
  endpoint_unavailable: "queued: all eligible phone numbers are at capacity",
};

export function capacityWaitLabel(
  capacityWaitCause: CapacityWaitCause | null | undefined,
): string {
  return capacityWaitCause == null
    ? "queued: waiting for capacity"
    : CAUSE_LABELS[capacityWaitCause];
}

export function CapacityWaitStatus({
  isWaitingForCapacity,
  capacityWaitCause,
}: CapacityWaitStatusProps) {
  if (!isWaitingForCapacity) {
    return null;
  }
  return <Badge tone="warning">{capacityWaitLabel(capacityWaitCause)}</Badge>;
}
